import { of, combineLatest } from 'rxjs'
import { map } from 'rxjs/operators'
import { toSectionHtml } from 'front-end-core/helpers/xml'
import url from 'front-end-core/core/url'
import { mapResponse, isValidResponse } from 'front-end-core/helpers/dataflow'
import { capitalize, first, last } from 'libraries/lodash'
import { QA_CONTENT, LEGISLATION, QA_MENU, LEGISLATION_MENU } from 'constants/controllers'
import { requestReferenceNodes as requestLegislation } from './LegislationReferenceService'
import { requestQAReferences as requestQA } from './QAService'

const mapLegislationReferences = function ({ aol, flavour, commentNodes, references }) {
  return commentNodes.map(commentNode => {
    const { reference: referenceId } = commentNode
    const { context, nodes, root } = references.find(reference => reference.root === referenceId)
    const { id, title, content } = nodes[root]
    const toTitle = ({ number, level, title }) => !number
      ? title || ''
      : `${capitalize(level)} ${number || ''}. ${title || ''}`

    const trimmedContext = title ? context : context.slice(0, -1)

    return {
      commentNode,
      commentPath: [
        {
          title: capitalize(aol),
          link: url(LEGISLATION_MENU, { aol })
        },
        ...trimmedContext.map(({ id, title, level, number }) => ({
          title: title || toTitle({ title, level, number }),
          link: url(LEGISLATION, { aol, flavour, path: id }),
          permaLink: url(LEGISLATION, { aol, flavour, path: id }, {}, true)
        }))
      ],
      commentReference: {
        id: root,
        title: title ? toTitle(nodes[root]) : toTitle(last(context)),
        content: toSectionHtml(content, 'span'),
        link: url(LEGISLATION, { aol, flavour, path: id }),
        permaLink: url(LEGISLATION, { aol, flavour, path: id }, {}, true)
      }
    }
  })
}

const mapQAReferences = function ({ aol, flavour, commentNodes, references }) {
  const { questions, sections } = references

  return commentNodes.reduce((acc, curr) => {
    const { reference } = curr

    if (!questions[reference]) {
      return acc
    }

    const { answer, questionNumber, question, sectionId, tocId } = questions[reference]
    const { path } = sections[sectionId] || { path: [] }

    return [
      ...acc,
      {
        commentNode: curr,
        commentPath: [
          {
            title: capitalize(aol),
            link: url(QA_MENU, { aol, flavour })
          },
          ...path.map(id => ({
            title: sections[id].title,
            link: url(QA_CONTENT, { aol, flavour, sectionId: first(path), focusedId: first(path) === id ? undefined : id }),
            permaLink: url(QA_CONTENT, { aol, flavour, sectionId: first(path), focusedId: first(path) === id ? undefined : id }, {}, true)
          }))
        ],
        commentReference: {
          id: tocId,
          title: `Q ${questionNumber}. ${question}`,
          content: answer,
          link: url(QA_CONTENT, { aol, flavour, sectionId: first(path), focusedId: tocId }),
          permaLink: url(QA_CONTENT, { aol, flavour, sectionId: first(path), focusedId: tocId }, {}, true)
        }
      }
    ]
  }, [])
}

const requestAolReferences = (aolGroupedComments, requestFn) => {
  const references = Object.keys(aolGroupedComments).map(aol =>
    requestFn({ aol, references: aolGroupedComments[aol].references })
  )

  return combineLatest([references]).pipe(
    map(references => {
      const invalidReferenceIndex = references.find(reference => !isValidResponse(reference))
      if (invalidReferenceIndex) {
        return references[invalidReferenceIndex]
      } else {
        return references.flat()
      }
    })
  )
}

export const requestCommentsReference = function ({ flavour, category, comments }) {
  if (comments.length === 0) {
    return of(comments)
  }

  const groupedComments = comments.reduce((acc, { commentNode }) => {
    const { aol, reference } = commentNode
    const hasAol = acc[aol] !== undefined

    if (hasAol) {
      return {
        ...acc,
        [aol]: {
          references: [...acc[aol].references, reference],
          commentNodes: [...acc[aol].commentNodes, commentNode]
        }
      }
    }

    return {
      ...acc,
      [aol]: {
        references: [reference],
        commentNodes: [commentNode]
      }
    }
  }, {})

  switch (category) {
    case 'legislation':
      return requestAolReferences(
        groupedComments,
        ({ aol, references }) => (
          requestLegislation({ references, aol }).pipe(
            map(mapResponse(references => mapLegislationReferences({
              aol,
              flavour,
              commentNodes: groupedComments[aol].commentNodes,
              references
            })))
          )
        )
      )
    case 'qa':
      return requestAolReferences(
        groupedComments,
        ({ aol, references }) => (
          requestQA({ flavour, references, aol }).pipe(
            map(mapResponse(references => mapQAReferences({
              aol,
              flavour,
              commentNodes: groupedComments[aol].commentNodes,
              references
            })))
          )
        )
      )
    default:
      return of(comments)
  }
}
